import {
  addDays,
  addMonths,
  INVOICE_HORIZON_DAYS,
  type FinanceContract,
  type FinanceInvoice,
  type FinanceReceipt,
  type RevenueGoal,
} from "./contracts";

/**
 * Amostra financeira do modo demo.
 *
 * O protótipo Zênite trazia 48 contratos inventados em `localStorage`; aqui a
 * amostra é derivada dos leads que já existem em `demo-data.ts`, tipada e
 * gerada pelas mesmas funções de calendário do domínio. Assim a Home, Clientes
 * e Análise mostram números que batem com as faturas exibidas no perfil do
 * lead, em vez de dois conjuntos de dados que se contradizem.
 *
 * A amostra é relativa a `today` de propósito: um demo com datas fixas envelhece
 * e passa a exibir "tudo atrasado" alguns meses depois.
 *
 * Isto é dado de desenvolvimento e QA (AGENTS §8). Nada aqui roda em produção.
 */

/** Comportamento de pagamento que cada contrato demonstra na interface. */
type PaymentBehavior =
  /** Todas as parcelas vencidas foram recebidas no dia. */
  | "em-dia"
  /** A última parcela vencida continua em aberto. */
  | "atrasado"
  /** A última parcela vencida recebeu metade do valor. */
  | "parcial"
  /** Contrato encerrado; as parcelas do período foram todas recebidas. */
  | "encerrado";

interface ContractSeed {
  leadId: string;
  membershipId?: string;
  kind: "monthly" | "oneoff";
  description: string;
  amountCents: number;
  /** Há quantos meses o contrato começou. */
  monthsAgo: number;
  /** Dia do vencimento; recua para o último dia em meses curtos. */
  dueDay: number;
  /** Origem do lead na assinatura, preservada para a receita por canal. */
  sourceSnapshot: string;
  behavior: PaymentBehavior;
  /** Meses de vigência até o encerramento, quando `behavior` é "encerrado". */
  monthsUntilCancelled?: number;
}

/**
 * Os leads são os mesmos de `demo-data.ts`. `l10` é o único com participação
 * ganha na amostra original, então ele é o contrato mais antigo e mais rico.
 */
const SEEDS: ContractSeed[] = [
  {
    leadId: "l10",
    membershipId: "m10",
    kind: "monthly",
    description: "Gestão de tráfego — plano completo",
    amountCents: 480_000,
    // Conversão dentro dos últimos 30 dias: a janela padrão de Clientes é essa,
    // e uma carteira inteira convertida há meses abre a tela sem nada para ler.
    monthsAgo: 1,
    dueDay: 20,
    sourceSnapshot: "Indicação",
    behavior: "em-dia",
  },
  {
    leadId: "l3",
    membershipId: "m3",
    kind: "monthly",
    description: "Gestão de tráfego — plano essencial",
    amountCents: 320_000,
    monthsAgo: 5,
    dueDay: 5,
    sourceSnapshot: "Indicação",
    behavior: "atrasado",
  },
  {
    leadId: "l1",
    membershipId: "m1",
    kind: "monthly",
    description: "Gestão de tráfego — plano essencial",
    amountCents: 350_000,
    monthsAgo: 3,
    dueDay: 15,
    sourceSnapshot: "Instagram",
    behavior: "parcial",
  },
  {
    leadId: "l6",
    membershipId: "m6",
    kind: "monthly",
    description: "Gestão de tráfego — imobiliário",
    amountCents: 520_000,
    monthsAgo: 11,
    dueDay: 20,
    sourceSnapshot: "Evento",
    behavior: "encerrado",
    monthsUntilCancelled: 7,
  },
  {
    leadId: "l9",
    membershipId: "m9",
    kind: "oneoff",
    description: "Campanha de lançamento",
    amountCents: 280_000,
    monthsAgo: 0,
    dueDay: 12,
    sourceSnapshot: "Instagram",
    behavior: "em-dia",
  },
  {
    leadId: "l11",
    membershipId: "m11",
    kind: "oneoff",
    description: "Diagnóstico e plano de mídia",
    amountCents: 180_000,
    monthsAgo: 0,
    dueDay: 25,
    sourceSnapshot: "LinkedIn",
    behavior: "atrasado",
  },
];

/** Mantém o mês de `date` e troca o dia, recuando no fim do mês curto. */
function withDay(date: string, day: number): string {
  const [year, month] = date.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const chosen = String(Math.min(day, lastDay)).padStart(2, "0");
  return `${date.slice(0, 7)}-${chosen}`;
}

function timestamp(date: string): string {
  return `${date}T12:00:00.000Z`;
}

export interface ClientConversion {
  /** Data em que o lead virou cliente: o recebimento que fechou a venda. */
  clientSince: string;
  convertedByReceiptId: string;
}

export interface FinanceFixtures {
  contracts: FinanceContract[];
  invoices: FinanceInvoice[];
  receipts: FinanceReceipt[];
  revenueGoals: RevenueGoal[];
  /** Conversões a aplicar nos leads demo, por id do lead. */
  conversions: Record<string, ClientConversion>;
}

/**
 * Monta a amostra financeira para a data informada.
 *
 * `today` é sempre parâmetro: nenhuma função do domínio lê o relógio por conta
 * própria, o que também torna a amostra testável.
 */
export function buildFinanceFixtures(today: string): FinanceFixtures {
  const contracts: FinanceContract[] = [];
  const invoices: FinanceInvoice[] = [];
  const receipts: FinanceReceipt[] = [];
  const conversions: Record<string, ClientConversion> = {};
  const horizon = addDays(today, INVOICE_HORIZON_DAYS);

  SEEDS.forEach((seed, index) => {
    const contractId = `fin-c${index + 1}`;
    const startOn = withDay(addMonths(today, -seed.monthsAgo), seed.dueDay);
    const cancelledOn =
      seed.behavior === "encerrado" && seed.monthsUntilCancelled !== undefined
        ? addMonths(startOn, seed.monthsUntilCancelled)
        : undefined;

    // As parcelas que este contrato tem no demo: a série mensal até o horizonte
    // (ou até o encerramento) e a parcela única do avulso.
    const dueDates: string[] = [];
    if (seed.kind === "oneoff") {
      dueDates.push(startOn);
    } else {
      const limit = cancelledOn && cancelledOn < horizon ? cancelledOn : horizon;
      for (let sequence = 0; ; sequence += 1) {
        const dueOn = addMonths(startOn, sequence);
        if (dueOn > limit) break;
        dueDates.push(dueOn);
      }
    }

    const dueBeforeToday = dueDates.filter((dueOn) => dueOn <= today);
    const lastDue = dueBeforeToday[dueBeforeToday.length - 1];

    dueDates.forEach((dueOn, position) => {
      const sequence = position + 1;
      const invoiceId = `${contractId}-i${sequence}`;
      invoices.push({
        id: invoiceId,
        contractId,
        leadId: seed.leadId,
        kind: seed.kind,
        sequence,
        description:
          seed.kind === "monthly" ? `Mensalidade ${sequence}` : seed.description,
        amountCents: seed.amountCents,
        dueOn,
        createdAt: timestamp(startOn),
      });

      if (dueOn > today) return;

      // Quanto desta parcela já foi recebido, conforme o comportamento.
      let amountCents = seed.amountCents;
      if (dueOn === lastDue) {
        if (seed.behavior === "atrasado") return;
        if (seed.behavior === "parcial") amountCents = Math.round(seed.amountCents / 2);
      }

      const receiptId = `${invoiceId}-r1`;
      const firstSale = sequence === 1;
      receipts.push({
        id: receiptId,
        invoiceId,
        contractId,
        leadId: seed.leadId,
        amountCents,
        receivedOn: dueOn,
        receivedAtTime: "12:00",
        firstSale,
        sourceSnapshot: seed.sourceSnapshot,
        idempotencyKey: receiptId,
        commandId: receiptId,
        createdAt: timestamp(dueOn),
      });

      if (firstSale) {
        conversions[seed.leadId] = { clientSince: dueOn, convertedByReceiptId: receiptId };
      }
    });

    const closedOn = conversions[seed.leadId]?.clientSince;
    contracts.push({
      id: contractId,
      leadId: seed.leadId,
      membershipId: seed.membershipId,
      kind: seed.kind,
      description: seed.description,
      amountCents: seed.amountCents,
      startOn,
      firstDueOn: startOn,
      cancelledOn,
      closedOn,
      sourceSnapshot: seed.sourceSnapshot,
      createdAt: timestamp(startOn),
      updatedAt: timestamp(closedOn ?? startOn),
      version: 1,
    });
  });

  return {
    contracts,
    invoices,
    receipts,
    revenueGoals: [buildQuarterGoal(today)],
    conversions,
  };
}

/** Meta do trimestre corrente, no mesmo recorte que a Análise usa. */
function buildQuarterGoal(today: string): RevenueGoal {
  const [year, month] = today.split("-").map(Number);
  const firstMonth = Math.floor((month - 1) / 3) * 3 + 1;
  const periodStart = `${year}-${String(firstMonth).padStart(2, "0")}-01`;
  const periodEnd = addDays(addMonths(periodStart, 3), -1);
  return {
    id: `fin-goal-${year}q${Math.floor((month - 1) / 3) + 1}`,
    periodStart,
    periodEnd,
    amountCents: 4_500_000,
    createdAt: timestamp(periodStart),
    updatedAt: timestamp(periodStart),
    version: 1,
  };
}
